/**
 * One dashboard (screenshots/84; brief §2.13 "A dashboard"; phone 91): breadcrumb, title + star, description,
 * then Live · Edit · Share · Print · ⋯; the dashboard filters with their coverage note; the page cross-filter bar; and the
 * Overview's own grid and widgets (OverviewGrid, WidgetFrame, Library, ChartEditorHost; nothing duplicated: openDashboard
 * points those stores at this dashboard). Edit = the Overview's customize mode (68): library drawer, drag/resize/keyboard,
 * autosave with versions (409 → merge, "Jordan is also editing", 90), Undo, Done. Phones: one column in reading order, no
 * Edit (91). From the handoff starter's src/analytics/DashboardPage.tsx.
 */
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { ChevronLeft, ChevronRight, Ellipsis, LayoutDashboard, Pencil, Plus, Printer, Share2, Star } from "lucide-react";
import { navigate, plainClick } from "../app/router";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import type { OrgRole, UserGroup } from "../nav/navConfig";
import { navStore, useNav } from "../nav/navStore";
import { Grid } from "../overview/OverviewGrid";
import { WidgetFrame } from "../overview/WidgetFrame";
import { Library } from "../overview/Library";
import { ChartEditorHost } from "../overview/ChartEditor";
import { CrossFilterBar, Presence } from "../overview/OverviewPage";
import { OvIcon } from "../overview/icons";
import { overviewLayout, overviewStore, setLayoutSaver, useOverview, useOverviewLayout } from "../overview/overviewStore";
import { mobileOrder } from "../overview/grid/layoutEngine";
import { canEdit, canShare, suggestionsFor, type Dashboard, type Viewer } from "./dashboards";
import type { WidgetType } from "../overview/types";
import { analyticsStore, useAnalytics } from "./analyticsStore";
import { CardMenu, type CardAction } from "./AnalyticsHome";
import { DashboardFilters, MobileFilterChips } from "./DashboardFilters";
import { ShareDialog } from "./ShareDialog";
import { PrintView } from "./PrintView";
import type { PageFilters } from "./pageFilters";
import { toastStore } from "../stores/toastStore";
import { widgetViews } from "../overview/widgetViews";
import type { DatasetResult } from "../overview/types";
import { Crumbs, sectionCrumb } from "../app/crumbs";

const failed = (e: unknown) => toastStore.getState().show({ message: (e as Error).message || "Something went wrong" });

export function DashboardPage({ id, group, role }: { id: string; group: UserGroup; role: OrgRole | null }) {
  const d = useAnalytics((s) => s.dashboards.find((x) => x.id === id));
  const viewer = useAnalytics((s) => s.snapshot?.me) as Viewer | undefined;
  const customizing = useOverviewLayout((s) => s.customizing);
  const filters = useOverview((s) => s.pageFilters);
  const [drawer, setDrawer] = useState(false);
  const [dialog, setDialog] = useState<"share" | "print" | null>(null);
  // Print shows the real widgets on its pages; the page's own grid steps aside meanwhile (one chart instance per widget).
  const [printViews, setPrintViews] = useState<Map<string, { ds: DatasetResult; title: string }>>(new Map());
  const layout = useOverviewLayout((s) => s.layout);
  const orgName = useAnalytics((s) => s.snapshot?.me.orgName ?? null);
  const starred = useNav((s) => s.prefs.starred.includes(id));
  const editable = !!d && !!viewer && canEdit(d, viewer);
  const phone = usePhone();

  // Right after Create (`?edit=1`): open in Edit mode with the widget library (desktop only), then drop the flag.
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get("print") === "1") { setPrintViews(new Map(widgetViews)); setDialog("print"); }
    if (qs.get("edit") === "1" && editable && matchMedia("(min-width: 768px)").matches) { overviewLayout.getState().setCustomizing(true); setDrawer(true); }
    if (qs.has("edit") || qs.has("print")) history.replaceState(null, "", location.pathname + location.hash);
    return () => { overviewLayout.getState().setCustomizing(false); setLayoutSaver(null); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.documentElement.classList.toggle("ov-drawer-open", customizing && drawer);
    return () => document.documentElement.classList.remove("ov-drawer-open");
  }, [customizing, drawer]);
  useEffect(() => {
    if (!customizing) return;
    const k = (e: KeyboardEvent) => {
      const s = overviewLayout.getState();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") { e.preventDefault(); if (e.shiftKey) s.redo(); else s.undo(); }
      else if (e.key === "Escape" && !(e.target as HTMLElement).closest?.(".ov-widget, input")) overviewStore.getState().select(null);
    };
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, [customizing]);

  if (!d || !viewer) return null;
  const setFilters = (f: PageFilters) => {
    overviewStore.getState().setPageFilters(f);
    if (editable) void analyticsStore.getState().saveMeta(d.id, { filters: f }).catch(failed);
  };
  const onAction = (kind: CardAction, x: Dashboard, title?: string) => {
    const s = analyticsStore.getState();
    if (kind === "duplicate") void s.duplicate(x.id).then((c) => navigate(`/dashboard/analytics/${c.id}`)).catch(failed);
    if (kind === "rename" && title) void s.saveMeta(x.id, { title }).catch(failed);
    if (kind === "delete") void s.remove(x.id).then(() => {
      if (navStore.getState().prefs.starred.includes(x.id)) navStore.getState().toggleStar(x.id, x.title);
      navigate("/dashboard/analytics");
    }).catch(failed);
  };
  const done = () => { overviewLayout.getState().setCustomizing(false); overviewStore.getState().select(null); setDrawer(false); };
  const star = <button className={`an-star an-star--title${starred ? " is-on" : ""}`} aria-pressed={starred} aria-label={starred ? "Unstar" : "Star"}
    data-tip={starred ? "Unstar · remove from sidebar" : "Star · add to sidebar"} onClick={() => navStore.getState().toggleStar(d.id, d.title)}><Star size={18} aria-hidden /></button>;

  return (
    <div className={`ov-page an-dash${customizing ? " is-customizing" : ""}`}>
      <header className="an-dash__head">
        <div className="an-dash__title">
          <Crumbs trail={[sectionCrumb("analytics"), { label: d.title }]} />
          <div className="an-dash__h1">
            <h1 className="an-title">{d.title}</h1>
            {star}
          </div>
          {d.description && <p className="an-sub">{d.description}</p>}
        </div>
        {customizing ? <EditToolbar onDone={done} /> : (
          <div className="an-dash__actions">
            <ConnectionStatus />
            {editable && <button className="btn btn--secondary" onClick={() => { overviewLayout.getState().setCustomizing(true); setDrawer(true); }}><Pencil size={15} aria-hidden />Edit</button>}
            <button className="btn btn--secondary" onClick={() => setDialog("share")}><Share2 size={15} aria-hidden />Share</button>
            <button className="btn btn--secondary" onClick={() => { setPrintViews(new Map(widgetViews)); setDialog("print"); }}><Printer size={15} aria-hidden />Print</button>
            <CardMenu d={d} canDelete={canShare(d, viewer)} canRename={editable} onAction={onAction} />
          </div>
        )}
      </header>
      <MobileHead d={d} star={star} onShare={() => setDialog("share")} onAction={onAction} canDelete={canShare(d, viewer)} canRename={editable} />
      <DashboardFilters filters={filters} types={d.layout.map((i) => i.type)} onChange={setFilters} />
      <MobileFilterChips filters={filters} />
      {customizing && <Presence />}
      <CrossFilterBar />
      {dialog === "print" ? null : phone ? <MobileGrid group={group} /> : (
        <div className="an-grid-host">
          <Grid group={group} fluid />
          {layout.length === 0 && editable && <EmptyDashboard group={group} role={role} onAdd={(type) => {
            if (!customizing) { overviewLayout.getState().setCustomizing(true); setDrawer(true); }
            overviewStore.getState().addWidget(type);
          }} />}
        </div>
      )}
      {customizing && drawer && <Library group={group} role={role} onClose={() => setDrawer(false)} />}
      <ChartEditorHost />
      {dialog === "share" && <ShareDialog dashboard={d} viewer={viewer} onClose={() => setDialog(null)} />}
      {dialog === "print" && <PrintView dashboard={{ ...d, layout }} orgName={orgName} filters={filters} views={printViews} onClose={() => setDialog(null)}
        onSaveSetup={(print) => { if (editable) void analyticsStore.getState().saveMeta(d.id, { print }).catch(failed); }} />}
    </div>
  );
}

/** Below 768px (mobile.css): phones get the one-column list instead of the grid (91). */
export function usePhone() {
  const q = typeof matchMedia === "undefined" ? null : matchMedia("(max-width: 767px)");
  const [phone, setPhone] = useState(!!q?.matches);
  useEffect(() => {
    if (!q) return;
    const on = () => setPhone(q.matches);
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return phone;
}

/** Edit mode toolbar (the Overview's customize toolbar, 68, without Reset): Saved · Undo · Done. */
function EditToolbar({ onDone }: { onDone: () => void }) {
  const save = useOverviewLayout((s) => s.status);
  const canUndo = useOverviewLayout((s) => s.past.length > 0);
  return (
    <div className="ov-head__actions ov-toolbar an-dash__actions">
      <span className="ov-save" role="status" data-state={save}>
        {save === "saving" ? <><OvIcon name="loader" className="ov-save__spin" />Saving…</> : save === "error" ? <>Not saved</> : <><OvIcon name="cloud-check" />Saved</>}
      </span>
      <button className="ov-icon-btn" aria-label="Undo" disabled={!canUndo} onClick={() => overviewLayout.getState().undo()}><OvIcon name="undo" size={16} /></button>
      <button className="btn btn--primary" onClick={onDone}><OvIcon name="check" />Done</button>
    </div>
  );
}

/** Phone header (91): back · title · star · share · ⋯ (no Edit on phones). */
function MobileHead({ d, star, onShare, onAction, canDelete, canRename }: { d: Dashboard; star: React.ReactNode; onShare: () => void;
  onAction: (k: CardAction, d: Dashboard, t?: string) => void; canDelete: boolean; canRename: boolean }) {
  return (
    <header className="an-mhead">
      <a className="an-icon-btn" href="/dashboard/analytics" aria-label="Analytics" onClick={(e) => { if (!plainClick(e)) return; e.preventDefault(); navigate("/dashboard/analytics"); }}><ChevronLeft size={20} aria-hidden /></a>
      <h1>{d.title}</h1>
      {star}
      <button className="an-icon-btn" aria-label="Share" onClick={onShare}><Share2 size={18} aria-hidden /></button>
      <CardMenu d={d} canDelete={canDelete} canRename={canRename} onAction={onAction} icon={<Ellipsis size={18} aria-hidden />} />
    </header>
  );
}

/** Phones (91): the same widgets, one column, in reading order (mobileOrder: top to bottom, then left to right). */
export function MobileGrid({ group }: { group: UserGroup }) {
  const items = useOverviewLayout((s) => s.layout);
  const { feeds, snapshot } = useOverview(useShallow((s) => ({ feeds: s.feeds, snapshot: s.snapshot })));
  const ordered = useMemo(() => mobileOrder(items), [items]);
  return (
    <div className="an-mgrid">
      {ordered.map((it) => (
        // A phone column counts as 6 cells (colW 40): charts and tables show at M, everything else at its S summary (91).
        <WidgetFrame key={it.id} item={it} colW={40} group={group} feeds={feeds} snapshot={snapshot} customizing={false} selected={false} removing={false}
          className="an-mgrid__item" style={{ height: it.type === "chart" || it.type === "table" ? 205 : 100 }} />
      ))}
    </div>
  );
}

/** A new, empty dashboard (screenshots/116): three suggestions from the group's templates; the library stays open beside it. */
export function EmptyDashboard({ group, role, onAdd }: { group: UserGroup; role: OrgRole | null; onAdd: (type: WidgetType) => void }) {
  const picks = suggestionsFor(group, role);
  return (
    <section className="empty-dash" aria-labelledby="empty-dash-title">
      <span className="empty-dash__icon"><LayoutDashboard size={24} aria-hidden /></span>
      <h2 id="empty-dash-title">Add your first widget</h2>
      <p>Drag one from the library, or start with one of these.</p>
      <ul>{picks.map((w) => (
        <li key={w.type}><button onClick={() => onAdd(w.type)}><OvIcon name={w.icon} size={18} /><b>{w.title}</b><Plus size={16} aria-hidden /></button></li>))}</ul>
    </section>
  );
}
